import React, { useState, useEffect, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import dayjs from 'dayjs';
import apiService from '../../../../services/ApiService';
import { useAppStore } from '../../../../shared/stores/useAppStore';
import TailwindButton from '../../../Tailwind/TailwindButton';
import TailwindModal from '../../../Tailwind/TailwindModal';
import { cn } from '../../../../lib/utils';

// Function to generate a color based on the user's name
const generateAvatarColor = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 60%)`;
};

interface Comment {
  id: number;
  content: string;
  author_id: number;
  author_name: string;
  created_at: string;
  updated_at: string;
}

interface InputModelDataValueCommentPopoverProps {
  field: {
    id: number;
  };
}

const InputModelDataValueCommentPopover = ({ field }: InputModelDataValueCommentPopoverProps) => {
  const currentUser = useAppStore(s => s.currentUser);
  const [comments, setComments] = useState<Comment[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [newComment, setNewComment] = useState('');
  const [selectedCommentId, setSelectedCommentId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    apiService.getModelDataValueComments(field.id).then(async(response) => {
        if (response && response.ok) {
            setComments(await response.json());
        }
    })
    }, [field.id]);

  const handleEdit = (commentId: number) => {
    const comment = comments.find(c => c.id === commentId);
    if (comment) {
      setEditingId(commentId);
      setEditContent(comment.content);
    }
  };

  const handleSave = () => {
    apiService.updateComment(editingId, editContent).then(async(response) => {
        if (response) {
            const newComment = await response.json()
            setComments(comments.map(comment =>
                comment.id === editingId ? newComment : comment
            ));
            setEditingId(null);
        }
    })
  };

  const handleDeleteClick = (commentId: number) => {
    setSelectedCommentId(commentId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    apiService.deleteComment(selectedCommentId).then(async(response) => {
        if (response) {
            setComments(comments.filter(comment => comment.id !== selectedCommentId));
            setDeleteDialogOpen(false);
            setSelectedCommentId(null);
        }
    })
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedCommentId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const handleNewComment = () => {
    if (newComment.trim()) {
      apiService.postComment(field.id, newComment).then(async(response) => {
        if (response) {
            setComments([...comments, await response.json()])
            setNewComment('');
        }
      })
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM D, YYYY HH:mm');
  };

  const isCommentEdited = (comment: Comment) => {
    return comment.created_at !== comment.updated_at;
  };

  const isCurrentUserAuthor = (comment: Comment) => {
    return comment.author_id === currentUser?.id;
  };

  const getDisplayName = (comment: Comment) => {
    const userName = comment.author_name + (isCurrentUserAuthor(comment) ? ' (You)' : '');
    return `${userName}`;
  };

  return (
    <div>
      {/* Comments Button with Badge */}
      <button
        onClick={() => setIsModalOpen(true)}
        title="Comments"
        className="relative p-1 rounded hover:bg-slate-100 transition-colors"
      >
        <span
          className={cn(
            'material-symbols-outlined text-[18px]',
            comments.length > 0 ? 'text-black' : 'text-slate-400'
          )}
        >
          chat_bubble_outline
        </span>
        {comments.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 text-[9px] font-bold text-white bg-black rounded-full">
            {comments.length}
          </span>
        )}
      </button>

      {/* Comments Modal */}
      <TailwindModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Comments"
        size="lg"
      >
        <div className="flex flex-col -mx-6 -my-4">
          {/* Comments List */}
          <div className="flex-1 overflow-auto p-4 space-y-4 max-h-[400px]">
            {comments.map((comment) => (
              <div
                key={comment.id}
                className={cn(
                  'flex flex-col',
                  isCurrentUserAuthor(comment) ? 'items-end' : 'items-start'
                )}
              >
                {/* Comment Header */}
                <div
                  className={cn(
                    'flex items-center justify-between w-full mb-2',
                    isCurrentUserAuthor(comment) ? 'flex-row-reverse' : 'flex-row'
                  )}
                >
                  <div
                    className={cn(
                      'flex items-center gap-2',
                      isCurrentUserAuthor(comment) ? 'flex-row-reverse' : 'flex-row'
                    )}
                  >
                    {/* Avatar */}
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                      style={{ backgroundColor: generateAvatarColor(comment.author_name) }}
                    >
                      {comment.author_name[0].toUpperCase()}
                    </div>

                    {/* Author Name */}
                    <span className="text-sm font-semibold text-slate-900">
                      {getDisplayName(comment)}
                    </span>

                    {/* Created Date */}
                    <span className="text-xs text-slate-500">
                      {formatDate(comment.created_at)}
                    </span>
                  </div>

                  {/* Menu for Current User's Comments */}
                  {isCurrentUserAuthor(comment) && (
                    <Menu as="div" className="relative">
                      {({ open: menuOpen }) => (
                      <>
                      <Menu.Button className="p-1 hover:bg-slate-100 rounded transition-colors">
                        <span className="material-symbols-outlined text-[18px] text-slate-600">
                          more_vert
                        </span>
                      </Menu.Button>

                      <Transition
                        show={!!menuOpen}
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 mt-1 w-36 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden z-10">
                          <div className="py-1">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={() => handleEdit(comment.id)}
                                  className={cn(
                                    'flex items-center w-full px-3 py-2 text-sm text-left gap-2',
                                    active ? 'bg-slate-100 text-slate-900' : 'text-slate-700'
                                  )}
                                >
                                  <span className="material-symbols-outlined text-[18px]">edit</span>
                                  Edit
                                </button>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={() => handleDeleteClick(comment.id)}
                                  className={cn(
                                    'flex items-center w-full px-3 py-2 text-sm text-left gap-2',
                                    active ? 'bg-red-50 text-red-700' : 'text-red-600'
                                  )}
                                >
                                  <span className="material-symbols-outlined text-[18px]">delete</span>
                                  Delete
                                </button>
                              )}
                            </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Transition>
                      </>
                      )}
                    </Menu>
                  )}
                </div>

                {/* Comment Content */}
                <div
                  className={cn(
                    'max-w-[80%] rounded-xl p-3',
                    editingId === comment.id ? 'w-full' : 'w-auto',
                    isCurrentUserAuthor(comment)
                      ? 'bg-blue-50 self-end'
                      : 'bg-slate-100 self-start'
                  )}
                >
                  {editingId === comment.id ? (
                    <div className="w-full">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none mb-3"
                        rows={6}
                      />
                      <div className="flex justify-end gap-2">
                        <TailwindButton
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </TailwindButton>
                        <TailwindButton
                          variant="primary"
                          size="sm"
                          onClick={handleSave}
                        >
                          Save
                        </TailwindButton>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-slate-900 whitespace-pre-wrap break-words">
                        {comment.content}
                      </p>
                      {isCommentEdited(comment) && (
                        <p className="text-xs text-slate-500 mt-1">
                          Edited: {formatDate(comment.updated_at)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* New Comment Input */}
          <div className="p-4 border-t border-slate-200">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Type your comment..."
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none mb-2"
              rows={3}
            />
            <TailwindButton
              variant="primary"
              size="md"
              icon="send"
              iconPosition="right"
              onClick={handleNewComment}
              disabled={!newComment.trim()}
            >
              Post Comment
            </TailwindButton>
          </div>
        </div>
      </TailwindModal>

      {/* Delete Confirmation Dialog */}
      <TailwindModal
        isOpen={deleteDialogOpen}
        onClose={handleDeleteCancel}
        title="Confirm Deletion"
        size="sm"
        showCloseButton={false}
        footer={
          <>
            <TailwindButton
              variant="ghost"
              size="sm"
              onClick={handleDeleteCancel}
            >
              Cancel
            </TailwindButton>
            <TailwindButton
              variant="danger"
              size="sm"
              onClick={handleDeleteConfirm}
            >
              Delete
            </TailwindButton>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Are you sure you want to delete this comment?
        </p>
      </TailwindModal>
    </div>
  );
};

export default InputModelDataValueCommentPopover;
